/**
 * AFL strategy "template" decks — a handful of pre-built, format-legal 60-card
 * decks, each built around ONE strategy so a new player can see (and play) the
 * kinds of plans the card pool supports. They are surfaced on the deck list as
 * copyable templates ("Add to my decks") AND as one-click "Play vs bot" decks.
 *
 * Every deck is exactly 60 cards, ≤ 1 legendary, with rare-or-better PLAYERS as
 * singletons (the only copy cap) — guarded by tests/integration/
 * run-afl-template-decks.mjs, which validates each against AFL_PILOT_FORMAT.
 *
 * The strategies deliberately span the position model's axes:
 *   - lean INTO marking (power) / DENY marking (spoil + cap)
 *   - lean INTO position tiers / negate TIER (Even Footing) / negate POSITION
 *     entirely (Off the Magnets)
 *   - plus an environment-control brew and a speed/run-game deck.
 */

import type { DeckEntry } from './format.js';

export type TemplateDeck = {
  /** Stable key (used as the saved-deck default name slug). */
  key: string;
  /** Display name. */
  name: string;
  /** One-word/short strategy tag for the gallery chip. */
  strategy: string;
  /** One-line, player-facing description of the plan. */
  description: string;
  /** The 60-card list. */
  entries: DeckEntry[];
};

export const AFL_TEMPLATE_DECKS: readonly TemplateDeck[] = [
  // ───────────────────────────────────────────────────────────────────────
  // 1. Marking — win the ball in the air with power talls + rucks, then goal.
  {
    key: 'above-the-pack',
    name: 'Above the Pack',
    strategy: 'Marking',
    description:
      'Tall, powerful marks and ruckmen win the contest in the air; Contested Mark and Set Shot finish it. Marking contests are decided on POWER — so this deck is heavy with it.',
    entries: [
      // Power talls + rucks (the marking specialists).
      { cardId: 'player_forward_1', qty: 1 },   // Spearhead (rare, pwr 5)
      { cardId: 'player_forward_4', qty: 1 },   // Hall-of-Famer (LEGENDARY, pwr 5)
      { cardId: 'player_forward_5', qty: 1 },   // Centre-Half (rare, pwr 5)
      { cardId: 'player_tall_1', qty: 4 },      // Beanpole (uncommon, pwr 5)
      { cardId: 'player_forward_9', qty: 4 },   // Deep Target (common, pwr 4)
      { cardId: 'player_forward_8', qty: 3 },   // Lead-Up (leading targets)
      { cardId: 'player_ruck_1', qty: 1 },      // Tower (rare, pwr 5 — ruck = marking specialist)
      { cardId: 'player_ruck_3', qty: 4 },      // Big Man (uncommon, pwr 5)
      { cardId: 'player_ruck_2', qty: 4 },      // Mobile Ruck (uncommon, pwr 4)
      { cardId: 'player_ruck_4', qty: 4 },      // Relief Ruck (common, pwr 4)
      { cardId: 'player_back_1', qty: 4 },      // Lockdown (uncommon, pwr 5)
      { cardId: 'player_back_4', qty: 4 },      // Hardhead (common, pwr 5)
      { cardId: 'player_back_5', qty: 1 },      // Anchor (rare, pwr 5)
      // Marking kit.
      { cardId: 'action_mark_attempt', qty: 6 }, // Contested Mark (power +2)
      { cardId: 'action_shot_on_goal', qty: 5 }, // Set Shot
      { cardId: 'action_long_bomb', qty: 4 },    // Long Bomb (power)
      { cardId: 'action_spoil', qty: 4 },        // deny their marks on defence too
      { cardId: 'stadium_twilight', qty: 2 },    // +1 skill for the shot
      { cardId: 'weather_clear', qty: 3 },       // keep the skies clear for your kicking
    ],
  },

  // ───────────────────────────────────────────────────────────────────────
  // 2. Deny marking — break their marks, scrap up the spill.
  {
    key: 'punch-it-through',
    name: 'Punch It Through',
    strategy: 'Anti-marking',
    description:
      "Spoil and Smother kill the opponent's marks, Chopped Off caps their gun's power, and rovers and small defenders win the loose ball that spills to ground.",
    entries: [
      { cardId: 'player_ruck_2', qty: 4 },      // Mobile Ruck (ruck-rover — wins BOTH contest types)
      { cardId: 'player_ruck_1', qty: 1 },      // Tower (rare — strong spoiling body)
      { cardId: 'player_ruck_3', qty: 4 },      // Big Man
      { cardId: 'player_mid_4', qty: 4 },       // Mosquito (rover — ground ball)
      { cardId: 'player_mid_9', qty: 4 },       // Scrapper (rover)
      { cardId: 'player_mid_3', qty: 1 },       // Ball-Magnet (epic)
      { cardId: 'player_mid_1', qty: 1 },       // Engine (rare)
      { cardId: 'player_back_2', qty: 4 },      // Tagger (small defender)
      { cardId: 'player_back_8', qty: 4 },      // Shadow (small defender)
      { cardId: 'player_back_4', qty: 4 },      // Hardhead (pwr 5 spoiler)
      { cardId: 'player_back_1', qty: 4 },      // Lockdown (pwr 5)
      { cardId: 'player_back_5', qty: 1 },      // Anchor (rare)
      // Denial kit.
      { cardId: 'action_spoil', qty: 6 },       // Spoil (power +2 in the marking contest)
      { cardId: 'action_smother', qty: 5 },     // Smother (dock the kicker's skill)
      { cardId: 'call_chopped_off', qty: 5 },   // Set their marker's Power = 2
      { cardId: 'call_man_on_the_mark', qty: 4 }, // Negate their gun's tier in the contest
      { cardId: 'action_tackle', qty: 2 },      // win it back on the ground
      { cardId: 'weather_wet', qty: 2 },        // a greasy ball = fumbled marks, missed shots
    ],
  },

  // ───────────────────────────────────────────────────────────────────────
  // 3. Position tiers — specialists everywhere; biggest in-position bonus.
  {
    key: 'right-man-right-spot',
    name: 'Right Man, Right Spot',
    strategy: 'Position tiers',
    description:
      'Every player is a tier-1 specialist (full forward, centre, full back, the flanks). In position, they get the biggest tier bonus there is — so keep them in their spot and never let tier be negated.',
    entries: [
      { cardId: 'player_forward_1', qty: 1 },   // Full Forward (rare)
      { cardId: 'player_forward_5', qty: 1 },   // Centre Half-Forward (rare)
      { cardId: 'player_forward_4', qty: 1 },   // Tall Forward (LEGENDARY)
      { cardId: 'player_forward_6', qty: 6 },   // Forward Pocket
      { cardId: 'player_forward_7', qty: 6 },   // HF Flank
      { cardId: 'player_mid_1', qty: 1 },       // Centre (rare)
      { cardId: 'player_mid_5', qty: 1 },       // Centre (epic)
      { cardId: 'player_mid_2', qty: 6 },       // Wing
      { cardId: 'player_mid_6', qty: 6 },       // Wing
      { cardId: 'player_back_1', qty: 6 },      // Full Back
      { cardId: 'player_back_5', qty: 1 },      // Centre Half-Back (rare)
      { cardId: 'player_back_6', qty: 6 },      // HB Flank
      { cardId: 'player_back_7', qty: 6 },      // Back Pocket
      // Convert the in-position wins.
      { cardId: 'action_shot_on_goal', qty: 4 },
      { cardId: 'action_mark_attempt', qty: 4 },
      { cardId: 'action_spoil', qty: 2 },
      { cardId: 'weather_clear', qty: 2 },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────
  // 4. Position matters, TIER negated — Even Footing flattens the specialists.
  {
    key: 'even-footing',
    name: 'Even Footing',
    strategy: 'Tier negated',
    description:
      'Play Even Footing and Man on the Mark so only IN vs OUT of position matters — tier is ignored. Then run honest line players who are always in position: a journeyman in his spot now beats a champion out of his.',
    entries: [
      { cardId: 'player_mid_3', qty: 1 },       // Ball-Magnet (epic, midfield line)
      { cardId: 'player_mid_7', qty: 4 },       // Grunt (midfield)
      { cardId: 'player_mid_8', qty: 4 },       // Onballer (midfield)
      { cardId: 'player_mid_1', qty: 1 },       // Engine (rare, centre)
      { cardId: 'player_forward_3', qty: 4 },   // Goal Sneak (forward third)
      { cardId: 'player_forward_8', qty: 4 },   // Lead-Up (half forward line)
      { cardId: 'player_forward_9', qty: 4 },   // Deep Target (deep forward line)
      { cardId: 'player_forward_10', qty: 4 },  // Park Footballer (forward third)
      { cardId: 'player_back_10', qty: 4 },     // Journeyman (back third)
      { cardId: 'player_back_9', qty: 4 },      // Plodder (deep back line)
      { cardId: 'player_back_3', qty: 4 },      // Sweeper (half back line)
      { cardId: 'player_back_5', qty: 1 },      // Anchor (rare)
      // The tier-negation kit.
      { cardId: 'condition_even_footing', qty: 4 }, // tier ignored, leaguewide
      { cardId: 'call_man_on_the_mark', qty: 4 },   // tier ignored, this contest
      { cardId: 'action_shot_on_goal', qty: 4 },
      { cardId: 'action_mark_attempt', qty: 3 },
      { cardId: 'action_spoil', qty: 3 },
      { cardId: 'weather_clear', qty: 3 },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────
  // 5. Position IRRELEVANT — Off the Magnets; contests on raw stat alone.
  {
    key: 'off-the-magnets',
    name: 'Off the Magnets',
    strategy: 'Positions off',
    description:
      'Off the Magnets and Throw It on the Boot switch position off entirely — contests resolve on stat alone. So forget where anyone stands and just stack the highest POWER in the competition.',
    entries: [
      { cardId: 'player_forward_1', qty: 1 },   // Spearhead (rare, pwr 5)
      { cardId: 'player_forward_4', qty: 1 },   // Hall-of-Famer (LEGENDARY, pwr 5)
      { cardId: 'player_forward_5', qty: 1 },   // Centre-Half (rare, pwr 5)
      { cardId: 'player_tall_1', qty: 6 },      // Beanpole (uncommon, pwr 5)
      { cardId: 'player_ruck_1', qty: 1 },      // Tower (rare, pwr 5)
      { cardId: 'player_ruck_3', qty: 6 },      // Big Man (uncommon, pwr 5)
      { cardId: 'player_back_1', qty: 6 },      // Lockdown (uncommon, pwr 5)
      { cardId: 'player_back_4', qty: 6 },      // Hardhead (common, pwr 5)
      { cardId: 'player_back_5', qty: 1 },      // Anchor (rare, pwr 5)
      { cardId: 'player_forward_9', qty: 6 },   // Deep Target (common, pwr 4)
      { cardId: 'player_ruck_2', qty: 5 },      // Mobile Ruck (uncommon, pwr 4)
      { cardId: 'player_ruck_4', qty: 5 },      // Relief Ruck (common, pwr 4)
      { cardId: 'player_mid_3', qty: 1 },       // Ball-Magnet (epic, pwr 4)
      // The position-off kit.
      { cardId: 'condition_off_the_magnets', qty: 4 }, // position ignored, leaguewide
      { cardId: 'call_throw_it_on_the_boot', qty: 4 }, // position ignored, this contest
      { cardId: 'action_mark_attempt', qty: 3 },       // power +2 stacks on the stat-only contest
      { cardId: 'action_spoil', qty: 3 },
    ],
  },

  // ───────────────────────────────────────────────────────────────────────
  // 6. Environment control — engineer a low baseline, win on power in the muck.
  {
    key: 'rig-the-conditions',
    name: 'Rig the Conditions',
    strategy: 'Conditions',
    description:
      "Drown the game in rain, mud and night so nobody can kick straight, and Flood the speed out of it — then win the only thing left: the power contest in the air. Engineer the weather to suit your team.",
    entries: [
      { cardId: 'player_forward_5', qty: 1 },   // Centre-Half (rare, pwr 5)
      { cardId: 'player_forward_1', qty: 1 },   // Spearhead (rare, pwr 5)
      { cardId: 'player_tall_1', qty: 5 },      // Beanpole (pwr 5)
      { cardId: 'player_ruck_1', qty: 1 },      // Tower (rare, pwr 5)
      { cardId: 'player_ruck_3', qty: 5 },      // Big Man (pwr 5)
      { cardId: 'player_back_4', qty: 5 },      // Hardhead (pwr 5)
      { cardId: 'player_back_1', qty: 5 },      // Lockdown (pwr 5)
      { cardId: 'player_back_5', qty: 1 },      // Anchor (rare, pwr 5)
      { cardId: 'player_forward_9', qty: 5 },   // Deep Target (pwr 4)
      { cardId: 'player_ruck_4', qty: 4 },      // Relief Ruck (pwr 4)
      // The conditions kit — suppress skill + speed, leave power untouched.
      { cardId: 'weather_wet', qty: 4 },        // Wet Track (skill −4)
      { cardId: 'weather_windy', qty: 3 },      // Driving Rain (skill −3)
      { cardId: 'weather_overcast', qty: 2 },   // Overcast
      { cardId: 'stadium_quagmire', qty: 3 },   // −1 skill, all players
      { cardId: 'time_night', qty: 3 },         // failing light, −2 skill on shots
      { cardId: 'condition_flood', qty: 3 },    // set Speed = 2 (kill the run, not your marks)
      { cardId: 'action_mark_attempt', qty: 4 },// power +2 — your win condition
      { cardId: 'action_spoil', qty: 3 },       // power +2 on defence
      { cardId: 'stadium_bush', qty: 2 },       // −2 stamina, all (wear them down)
    ],
  },

  // ───────────────────────────────────────────────────────────────────────
  // 7. Run game — speed, Dash, Second Effort; win on the ground and on the burst.
  {
    key: 'dash-and-carry',
    name: 'Dash and Carry',
    strategy: 'Run game',
    description:
      'Quick, running players who beat the tackle and win the loose ball. Dash and Carry and Second Effort set the runner clear; keep the ball moving and never get caught. (Avoid Flood — it caps your speed.)',
    entries: [
      { cardId: 'player_mid_2', qty: 5 },       // Wing (speed 5)
      { cardId: 'player_mid_4', qty: 5 },       // Mosquito (speed 5, rover)
      { cardId: 'player_forward_2', qty: 5 },   // Crumber (speed 5)
      { cardId: 'player_forward_6', qty: 4 },   // Pocket Snipe (speed 5)
      { cardId: 'player_small_1', qty: 5 },     // Livewire (speed 5)
      { cardId: 'player_back_6', qty: 5 },      // Rebounder (speed 5)
      { cardId: 'player_forward_3', qty: 4 },   // Goal Sneak (speed 4)
      { cardId: 'player_mid_6', qty: 4 },       // Linkman (speed 4)
      { cardId: 'player_back_2', qty: 4 },      // Tagger (speed 4)
      { cardId: 'player_ruck_2', qty: 4 },      // Mobile Ruck (speed 4, ruck-rover)
      { cardId: 'player_mid_3', qty: 1 },       // Ball-Magnet (epic)
      { cardId: 'player_mid_1', qty: 1 },       // Engine (rare)
      // The run kit.
      { cardId: 'action_dash', qty: 5 },        // Dash and Carry (speed +2 on the run)
      { cardId: 'call_second_effort', qty: 4 }, // set Speed = 4 on the run
      { cardId: 'action_tackle', qty: 2 },      // speed +2 to win it back
      { cardId: 'call_second_wind', qty: 2 },   // ignore the late-game drain
    ],
  },
];

/** Total card count for a template (handy for the gallery + tests). */
export function templateDeckSize(deck: TemplateDeck): number {
  return deck.entries.reduce((n, e) => n + e.qty, 0);
}
